import React from 'react';
import { ListFieldSection } from '../../../Layout';
import { withInjectedProps } from '../../../Core/InjectedPropsContext';
import { useSelector } from 'react-redux';
import deepEquals from '../../../../Lib/Object/deepEquals';
import { orderBy, uniqBy as deprecatedUniqBy } from 'lodash';
import { getMessages, IApiDataModel } from 'Data/ViewModels/IApiDataModel';
import { IWorksheetComponent } from '../types';
import { combine } from 'Lib/Hooks/Selectors';
import { IMessageProps } from 'Components/Core';
import { messagePathsMatch } from '../Helpers/messageHelpers';
import { hasNonEmptyValue } from 'Lib/Any';
import { joinPaths } from 'Lib/Object/Path/shared';

/**
 * @deprecated Use ramda not lodash
 */
const uniqBy = deprecatedUniqBy;
type ContextCaption = { path: string; caption: string };

interface IWorksheetsResultsMessagesSectionProps extends IWorksheetComponent {
  contextCaptions?: ContextCaption[];
  excludeMessagePaths?: string[];
}

/**
 * Section that brings together messages from other parts of the worksheet that need to be shown in the results section.
 */
const WorksheetsResultsMessagesSectionBase: React.FC<IWorksheetsResultsMessagesSectionProps> = ({
  namePrefix = 'data',
  createWorksheetSelector,
  contextCaptions = [],
  excludeMessagePaths = [],
}) => {
  // Select messages from the API model and transform into message props.
  const messages = useSelector(
    combine(
      [createWorksheetSelector!(), (state) => state.messageLink.linkTargetIds],
      (data: IApiDataModel | undefined | null, linkTargetIds): IMessageProps[] => {
        if (!data) return [];

        const messages = getMessages(data).flatMap((messages) => {
          const basePath = joinPaths(namePrefix, messages.path);

          return messages.value
            .filter((m) => m.MessageType === 'error')
            .filter((m) => !excludeMessagePaths.some((e) => messagePathsMatch(joinPaths(basePath, m.PropertyName), e)))
            .map((m) => {
              const path = joinPaths(basePath, m.PropertyName);
              const context = contextCaptions.find((c) => path.startsWith(c.path));
              const linkToId = linkTargetIds.find((i) => messagePathsMatch(joinPaths(path, m.Id), i));
              const message = `${context ? `${context.caption}: ` : ''}${m.Text}`;

              return {
                linkToId,
                message,
                error: true,
              } as IMessageProps;
            });
        });

        //sorts so that messages having a link have priority, that is, they are not excluded in uniqBy
        const ordered = orderBy(messages, (x) => hasNonEmptyValue(x.linkToId));
        return uniqBy(ordered, (m) => m.message);
      }
    ),
    deepEquals
  );

  return hasNonEmptyValue(messages) ? <ListFieldSection messages={messages} /> : null;
};

const WorksheetsResultsMessagesSection = withInjectedProps(WorksheetsResultsMessagesSectionBase);

export { WorksheetsResultsMessagesSection };
export default WorksheetsResultsMessagesSection;
